import React from 'react';
import { useTranslation } from 'react-i18next';
import './WorkReportView.css';
import { WorkReport } from '../../types/catalogs';

interface WorkReportViewProps {
  workReport: WorkReport;
  onEdit?: () => void;
  onExportPDF: () => void;
  onClose: () => void;
}

export const WorkReportView = ({ workReport, onEdit, onExportPDF, onClose }: WorkReportViewProps) => {
  const { t } = useTranslation();
  const { attributes: data } = workReport;

  const weatherLabel = (code?: string) => {
    const labels: Record<string, string> = {
      DIRECTO: t('weatherDirect'),
      NUBLADO: t('weatherCloudy'),
      LLUVIA: t('weatherRain'),
      TORMENTA: t('weatherStorm'),
      VIENTO: t('weatherWind'),
    };
    return code ? (labels[code] || code) : '';
  };

  const surfaceLabel = (code?: string) => {
    const labels: Record<string, string> = {
      DIRECTO: t('weatherDirect'),
      HUMEDO: t('surfaceWet'),
      MOJADO: t('surfaceSoaked'),
      SECO: t('surfaceDry'),
    };
    return code ? (labels[code] || code) : '';
  };

  const groupingLabel = (code?: string) => {
    const labels: Record<string, string> = {
      EXTENDIDO: t('groupingLaid'),
      FRESADO: t('groupingMilling'),
      RIEGOS: t('groupingTackCoat'),
      COMPACTADO: t('groupingCompacted'),
      BARRIDO: t('groupingSwept'),
      SEÑALIZACION: t('groupingSignage'),
      RIEGO_AGLOMERADO: t('groupingAgglomerateCoat'),
    };
    return code ? (labels[code] || code) : '';
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('es-ES', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'draft':
        return <span className="status-badge draft">{t('draft')}</span>;
      case 'completed':
        return <span className="status-badge completed">{t('completed')}</span>;
      case 'approved':
        return <span className="status-badge approved">{t('approved')}</span>;
      default:
        return <span className="status-badge">{status}</span>;
    }
  };

  return (
    <div className="work-report-view">
      {/* Header */}
      <div className="report-header">
        <div className="header-content">
          <div className="report-title">
            <h1>{t('workReportTitle', { number: data.numero })}</h1>
            <div className="report-date">{formatDate(data.fecha)}</div>
          </div>
          <div className="header-actions">
            {getStatusBadge(data.status)}
            {onEdit && (
              <button onClick={onEdit} className="edit-btn">
                ✏️ {t('edit')}
              </button>
            )}
            <button onClick={onExportPDF} className="export-btn">
              📄 {t('exportPdf')}
            </button>
            <button onClick={onClose} className="close-btn">
              ✕
            </button>
          </div>
        </div>
      </div>

      <div className="report-content">
        {/* Project Information */}
        <div className="project-info">
          {data.project?.data && (
            <>
              <h2>{data.project.data.attributes.name}</h2>
              {data.project.data.attributes.description && <p>{data.project.data.attributes.description}</p>}
            </>
          )}
          {data.enviadoPor && <div className="sent-by">{t('sentBy')}: {data.enviadoPor}</div>}
          {data.temperaturaAmbiente && <div>{t('temperature')}: {data.temperaturaAmbiente}°C</div>}
          {data.ambiente && <div>{t('atmosphere')}: {weatherLabel(data.ambiente)}</div>}
          {data.estadoSuperficie && <div>{t('surfaceState')}: {surfaceLabel(data.estadoSuperficie)}</div>}
        </div>

        {/* Personal Propio */}
        {data.personalPropio && data.personalPropio.length > 0 && (
          <div className="section">
            <h3>{t('ownStaff')}</h3>
            <div className="table-container">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>{t('staff')}</th>
                    <th>{t('startTime')}</th>
                    <th>{t('endTime')}</th>
                    <th>{t('stopDuration')}</th>
                    <th>{t('grossShift')}</th>
                    <th>{t('netShift')}</th>
                    <th>{t('grouping')}</th>
                  </tr>
                </thead>
                <tbody>
                  {data.personalPropio.map((entry, index) => (
                    <tr key={index}>
                      <td>{entry.employee?.data?.attributes?.nombre} {entry.employee?.data?.attributes?.apellidos}</td>
                      <td>{entry.horaInicio}</td>
                      <td>{entry.horaFin}</td>
                      <td>{entry.duracionParadas} h</td>
                      <td>{entry.jornadaBruta} h</td>
                      <td>{entry.jornadaNeta} h</td>
                      <td>{groupingLabel(entry.agrupacion)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Maquinaria Propia */}
        {data.maquinariaPropia && data.maquinariaPropia.length > 0 && (
          <div className="section">
            <h3>{t('ownMachinery')}</h3>
            <div className="table-container">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>{t('ownMachinery')}</th>
                    <th>{t('days')}</th>
                  </tr>
                </thead>
                <tbody>
                  {data.maquinariaPropia?.map((entry, index) => (
                    <tr key={index}>
                      <td>{entry.machinery?.data?.attributes?.descripcion}</td>
                      <td>{t('daysUnit', { count: entry.dias })}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Maquinaria Alquilada */}
        {data.maquinariaAlquilada && data.maquinariaAlquilada.length > 0 && (
          <div className="section">
            <h3>{t('rentedMachinery')}</h3>
            <div className="table-container">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>{t('machine')}</th>
                    <th>{t('supplier')}</th>
                    <th>{t('days')}</th>
                    <th>{t('hours')}</th>
                  </tr>
                </thead>
                <tbody>
                  {data.maquinariaAlquilada?.map((entry, index) => (
                    <tr key={index}>
                      <td>{entry.maquina}</td>
                      <td>{entry.supplier?.data?.attributes?.nombre}</td>
                      <td>{t('daysUnit', { count: entry.dias })}</td>
                      <td>{entry.horas}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Temperatura Mezcla */}
        {data.temperaturaMezcla && data.temperaturaMezcla.length > 0 && (
          <div className="section">
            <h3>{t('mixTemperature')}</h3>
            <div className="table-container">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>{t('asfaltiaTrucks')}</th>
                    <th>{t('arrivalTime')}</th>
                    <th>{t('mixTemperature')}</th>
                  </tr>
                </thead>
                <tbody>
                  {data.temperaturaMezcla?.map((entry, index) => (
                    <tr key={index}>
                      <td>{entry.camion}</td>
                      <td>{entry.horaLlegada}</td>
                      <td>{entry.temperatura} ºC</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Climatología */}
        <div className="section">
          <h3>{t('weatherAt9')}</h3>
          <div className="table-container">
            <table className="data-table">
              <thead>
                <tr>
                  <th>{t('ambientTemp')}</th>
                  <th>{t('atmosphere')}</th>
                  <th>{t('surfaceState')}</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>{data.temperaturaAmbiente || '-'} ºC</td>
                  <td>{weatherLabel(data.ambiente) || '-'}</td>
                  <td>{surfaceLabel(data.estadoSuperficie) || '-'}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        {/* Camiones Fresando */}
        {data.camionesFresando && data.camionesFresando.length > 0 && (
          <div className="section">
            <h3>{t('millingTrucks')}</h3>
            <div className="table-container">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>{t('catalogTrucks')}</th>
                    <th>{t('hours')}</th>
                    <th>{t('trips')}</th>
                  </tr>
                </thead>
                <tbody>
                  {(data.camionesFresando || []).map((entry, index) => (
                    <tr key={index}>
                      <td>{entry.truck?.data?.attributes?.matricula}</td>
                      <td>{entry.horas} h</td>
                      <td>{entry.viajes}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Camiones Obra */}
        {data.camionesObra && data.camionesObra.length > 0 && (
          <div className="section">
            <h3>{t('workTrucks')}</h3>
            <div className="table-container">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>{t('catalogTrucks')}</th>
                    <th>{t('hours')}</th>
                  </tr>
                </thead>
                <tbody>
                  {(data.camionesObra || []).map((entry, index) => (
                    <tr key={index}>
                      <td>{entry.truck?.data?.attributes?.matricula}</td>
                      <td>{entry.horas}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Materiales Extendido */}
        {data.materialesExtendido && data.materialesExtendido.length > 0 && (
          <div className="section">
            <h3>{t('asfaltiaMaterials')}</h3>
            <div className="table-container">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>{t('asfaltiaMaterials')}</th>
                    <th>M2</th>
                    <th>TN</th>
                  </tr>
                </thead>
                <tbody>
                  {(data.materialesExtendido || []).map((entry, index) => (
                    <tr key={index}>
                      <td>{entry.material?.data?.attributes?.descripcion}</td>
                      <td>{entry.m2} M2</td>
                      <td>{entry.tn} TN</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Observaciones */}
        {data.observaciones && (
          <div className="section">
            <h3>{t('observations')}</h3>
            <div className="observations">
              {data.observaciones.split('\n').map((line, index) => (
                <p key={index}>{line}</p>
              ))}
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="report-footer">
          <div className="creation-info">
            <p>{t('createdOn')}: {formatDate(data.createdAt)}</p>
            {data.updatedAt !== data.createdAt && (
              <p>{t('lastModified')}: {formatDate(data.updatedAt)}</p>
            )}
          </div>
          <div className="powered-by">
            <p>Powered by DIGITU</p>
          </div>
        </div>
      </div>
    </div>
  );
};